import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { sourceShortLabel } from "../features/leads/leadFilterLabels";
import { Card } from "../components/ui/Card";
import { StatusPill } from "../components/ui/StatusPill";
import { LastActivityCell } from "../features/activities/LastActivityCell";
import { useWorkspace } from "../features/workspace/WorkspaceContext";
import { useAuth } from "../features/auth/AuthProvider";
import { useMemberDetail } from "../features/members/api";
import { formatRelative } from "../lib/format";
import { RangePills } from "./MembersPage";

type Range = 7 | 30 | 90;

const STATUS_ORDER = [
  "new",
  "interested",
  "follow_up",
  "not_interested",
  "converted",
  "lost",
];
const STATUS_COLORS: Record<string, string> = {
  new: "#3b82f6",
  interested: "#8b5cf6",
  follow_up: "#f59e0b",
  not_interested: "#94a3b8",
  converted: "#16a34a",
  lost: "#dc2626",
};
const SOURCE_COLORS = [
  "#3b82f6",
  "#16a34a",
  "#8b5cf6",
  "#f59e0b",
  "#dc2626",
  "#0ea5e9",
  "#ec4899",
  "#64748b",
];

export default function MemberDetailPage() {
  const { user } = useAuth();
  const { userId } = useParams<{ userId: string }>();
  const { current } = useWorkspace();
  const [days, setDays] = useState<Range>(30);

  const denied = !!user && !user.isSuperadmin && user.role === "sales";
  const { data, isLoading, isError, error } = useMemberDetail(
    denied ? null : current?.slug ?? null,
    userId,
    days
  );
  if (denied) return <Navigate to="/dashboard" replace />;

  if (isLoading) {
    return (
      <div className="page">
        <header className="page-header">
          <h1 className="page-title">Member</h1>
        </header>
        <div className="skeleton-rows">Loading…</div>
      </div>
    );
  }
  if (isError) {
    return (
      <div className="page">
        <div className="form-error">{(error as Error).message}</div>
      </div>
    );
  }
  if (!data) return null;

  const { metrics, funnel, sources, activityTypes, recentActivities, assignedLeads } = data;
  const initials = data.user.name
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const funnelRows = STATUS_ORDER.map((s) => ({
    status: s,
    label: s.replace("_", " "),
    count: funnel.find((f) => f.status === s)?.count ?? 0,
  }));
  const funnelMax = Math.max(1, ...funnelRows.map((f) => f.count));
  const funnelTotal = funnelRows.reduce((sum, r) => sum + r.count, 0);

  const sourceRows = sources.map((s) => ({
    name: s.campaign ?? `${sourceShortLabel(s.source)} (uncategorised)`,
    value: s.count,
  }));
  const sourceTotal = sourceRows.reduce((sum, r) => sum + r.value, 0);

  const typeTotal = activityTypes.reduce((sum, r) => sum + r.count, 0);
  const typeMax = Math.max(1, ...activityTypes.map((r) => r.count));

  const lastActiveLabel = metrics.lastActivityAt
    ? formatRelative(metrics.lastActivityAt)
    : null;

  return (
    <div className="page">
      <div className="member-hero">
        <div className="member-hero-identity">
          <div className="member-hero-avatar" aria-hidden>
            {initials || "?"}
          </div>
          <div className="member-hero-body">
            <h1 className="member-hero-name">
              <span>{data.user.name}</span>
              <span
                className={`member-hero-status ${
                  data.user.isActive ? "is-active" : "is-inactive"
                }`}
              >
                <span className="dot" />
                {data.user.isActive ? "Active" : "Deactivated"}
              </span>
            </h1>
            <div className="member-hero-meta">
              <span className="badge">{data.user.role}</span>
              <span className="sep" aria-hidden />
              <a href={`mailto:${data.user.email}`}>{data.user.email}</a>
              {lastActiveLabel ? (
                <>
                  <span className="sep" aria-hidden />
                  <span title={metrics.lastActivityAt ?? ""}>
                    Last active {lastActiveLabel}
                  </span>
                </>
              ) : (
                <>
                  <span className="sep" aria-hidden />
                  <span>No activity yet</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="member-hero-actions">
          <RangePills value={days} onChange={setDays} />
          <Link to="/members" className="btn btn-secondary btn-sm">
            ← All members
          </Link>
        </div>
      </div>

      <div className="stat-grid">
        <StatTile
          label="Active leads"
          value={metrics.activeLeads.toLocaleString()}
        />
        <StatTile
          label="New leads"
          value={metrics.newLeads.toLocaleString()}
        />
        <StatTile
          label="Activities"
          value={metrics.activitiesLogged.toLocaleString()}
        />
        <StatTile
          label="Converted"
          value={metrics.converted.toLocaleString()}
          accent="ok"
        />
        <StatTile
          label="Conversion rate"
          value={
            metrics.newLeads === 0
              ? "—"
              : `${(metrics.conversionRate * 100).toFixed(1)}%`
          }
        />
        <StatTile
          label="Stale >24h"
          value={metrics.unattended24h.toLocaleString()}
          accent={metrics.unattended24h > 0 ? "warn" : undefined}
        />
        {/* Avg first reply intentionally hidden — kept in the API payload so
            it can be re-enabled without a backend change. Last active moved
            into the page header. */}
      </div>

      <div className="member-detail-grid">
        <Card
          title="Funnel"
          subtitle={`${funnelTotal.toLocaleString()} leads received in range`}
        >
          {funnelTotal === 0 ? (
            <p className="muted">No leads received in this range.</p>
          ) : (
            <ul className="funnel-list">
              {funnelRows.map((row) => {
                const pctOfMax = (row.count / funnelMax) * 100;
                const pctOfTotal =
                  funnelTotal > 0 ? (row.count / funnelTotal) * 100 : 0;
                return (
                  <li key={row.status} className="funnel-row">
                    <div className="funnel-row-head">
                      <span className="funnel-label">{row.label}</span>
                      <span className="funnel-count">
                        {row.count.toLocaleString()}
                        <span className="muted"> · {pctOfTotal.toFixed(0)}%</span>
                      </span>
                    </div>
                    <div className="funnel-bar-track">
                      <div
                        className="funnel-bar-fill"
                        style={{
                          width: `${pctOfMax}%`,
                          background: STATUS_COLORS[row.status] ?? "#3b82f6",
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card
          title="Top sources"
          subtitle={`${sourceTotal.toLocaleString()} leads · top ${sourceRows.length}`}
        >
          {sourceRows.length === 0 ? (
            <p className="muted">No sources to break down yet.</p>
          ) : (
            <ul className="source-list">
              {sourceRows.map((s, i) => {
                const pct = sourceTotal > 0 ? (s.value / sourceTotal) * 100 : 0;
                const color = SOURCE_COLORS[i % SOURCE_COLORS.length];
                return (
                  <li key={`${s.name}-${i}`} className="source-row">
                    <div className="source-row-head">
                      <span className="source-name">
                        <span
                          className="source-swatch"
                          style={{ background: color }}
                        />
                        {s.name}
                      </span>
                      <span className="source-count">
                        {s.value.toLocaleString()}
                        <span className="muted"> · {pct.toFixed(0)}%</span>
                      </span>
                    </div>
                    <div className="funnel-bar-track">
                      <div
                        className="funnel-bar-fill"
                        style={{ width: `${pct}%`, background: color }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <div className="member-detail-grid">
        <Card
          title="Activity mix"
          subtitle={`${typeTotal.toLocaleString()} activities in range`}
        >
          {activityTypes.length === 0 ? (
            <p className="muted">No activity logged in this range.</p>
          ) : (
            <ul className="funnel-list">
              {activityTypes.map((t, i) => {
                const pctOfMax = (t.count / typeMax) * 100;
                const pctOfTotal = typeTotal > 0 ? (t.count / typeTotal) * 100 : 0;
                const color = SOURCE_COLORS[i % SOURCE_COLORS.length];
                return (
                  <li key={t.type} className="funnel-row">
                    <div className="funnel-row-head">
                      <span className="funnel-label">{t.type}</span>
                      <span className="funnel-count">
                        {t.count.toLocaleString()}
                        <span className="muted"> · {pctOfTotal.toFixed(0)}%</span>
                      </span>
                    </div>
                    <div className="funnel-bar-track">
                      <div
                        className="funnel-bar-fill"
                        style={{ width: `${pctOfMax}%`, background: color }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card
          title="Recent activity"
          subtitle="Latest 15 actions across all leads"
        >
          {recentActivities.length === 0 ? (
            <p className="muted">No activity yet.</p>
          ) : (
            <div className="activity-timeline">
              {recentActivities.map((a) => (
                <div key={a.id} className="activity-timeline-item">
                  <div className="activity-timeline-dot" />
                  <div className="activity-timeline-body">
                    <div>
                      <span className="activity-timeline-type">{a.type}</span>
                      {a.outcome ? (
                        <span className="muted">· {a.outcome}</span>
                      ) : null}
                    </div>
                    {a.body ? <div style={{ marginTop: 2 }}>{a.body}</div> : null}
                    <div className="activity-timeline-meta">
                      on{" "}
                      <Link to={`/leads/${a.leadId}`} className="link">
                        {a.leadName ?? "(unnamed lead)"}
                      </Link>
                    </div>
                  </div>
                  <div
                    className="activity-timeline-time"
                    title={a.occurredAt}
                  >
                    {formatRelative(a.occurredAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card
        title="Currently assigned"
        subtitle={`${assignedLeads.length} open lead${assignedLeads.length === 1 ? "" : "s"} (excluding converted/lost)`}
        right={
          <Link
            to={`/leads?assignee=${data.user.id}`}
            className="btn btn-secondary btn-sm"
          >
            Open in Leads →
          </Link>
        }
      >
        {assignedLeads.length === 0 ? (
          <p className="muted">No open leads assigned right now.</p>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Last activity</th>
                  <th>Received</th>
                </tr>
              </thead>
              <tbody>
                {assignedLeads.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link to={`/leads/${row.id}`} className="row-link">
                        {row.name ?? "(no name)"}
                      </Link>
                      {row.company ? (
                        <div className="row-sub">{row.company}</div>
                      ) : null}
                    </td>
                    <td>
                      <StatusPill status={row.status} />
                      <LastActivityCell
                        leadId={row.id}
                        type={row.lastLoggedActivityType}
                        at={row.lastLoggedActivityAt}
                      />
                    </td>
                    <td>
                      <span className="badge">
                        {sourceShortLabel(row.source)}
                      </span>
                    </td>
                    <td title={row.lastActivityAt ?? ""}>
                      {row.lastActivityAt
                        ? formatRelative(row.lastActivityAt)
                        : "—"}
                    </td>
                    <td title={row.createdAt}>{formatRelative(row.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function StatTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "ok" | "warn" | "err";
}) {
  return (
    <div className={`stat-tile ${accent ? `accent-${accent}` : ""}`.trim()}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}
